/**
 * FAQs rendered on the homepage and pricing page AND emitted as FAQPage JSON-LD
 * (client: src/utils/blogSeo.js, build: crawler shell + scripts/prerender-seo.mjs).
 * One source so the structured data always matches the visible answers.
 * Answers may use [label](/path) links; schema text strips them.
 * Copy rules: product counts only (8 mocks, 720+ questions, 90-question Mock 1),
 * no prices, no pass rates, "mock test" only in question wording.
 */
import { stripInlineLinks } from '../utils/inlineLinks';
import { SITE, canonicalUrl } from '../seo/urls';

export const HOME_FAQS = [
  {
    q: 'Is the CFA Level 1 mock exam free?',
    a: 'Yes. Mock Exam 1 is free, no account needed: a timed 90-question session with an instant score. No credit card is needed to start.',
  },
  {
    q: 'How many CFA mock exams come with paid access?',
    a: 'Paid Level 1 access unlocks all 8 timed mock exams, built from a bank of 720+ questions, with explanations, a topic breakdown and post-mock analysis.',
  },
  {
    q: 'Are these official CFA Institute mock tests?',
    a: 'No. CFA Mock Exam is independent practice software and is not affiliated with CFA Institute. Registered candidates also get official practice questions and mock exams from CFA Institute.',
  },
  {
    q: 'How should I use a CFA practice exam?',
    a: 'Sit it in one timed block, log every miss with the reason it happened, and revisit those topics before your next mock exam. The [CFA practice exam guide](/cfa-practice-exam/) explains when to start and how to review.',
  },
];

export const PRICING_FAQS = [
  {
    q: 'Can I try a CFA mock exam before paying?',
    a: 'Yes. Mock Exam 1 is free, no account needed, and gives you an instant score. Pay only if you want the remaining mock exams, explanations and analysis.',
  },
  {
    q: 'What does paid Level 1 access include?',
    a: 'All 8 timed mock exams from a 720+ question bank, a topic breakdown, explanations, post-mock analysis and topic drills while access is active.',
  },
  {
    q: 'What is the difference between lifetime and monthly access?',
    a: 'Lifetime access is a one-time payment that never renews. Monthly access renews until you cancel, and access runs to the end of the paid period.',
  },
  {
    q: 'Does the price include CFA Institute exam fees?',
    a: 'No. Exam registration fees are paid only to CFA Institute. See [CFA Level 1 exam fees explained](/cfa-level-1-exam-fees/) for how those fees work.',
  },
];

export function faqPageSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: stripInlineLinks(a) },
    })),
  };
}

/** Home → … → page. `trail` is [{ name, path }] after Home; items use canonical URLs. */
export function breadcrumbSchema(trail) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{ name: 'Home', path: '/' }, ...trail].map(({ name, path }, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name,
      item: path === '/' ? `${SITE}/` : canonicalUrl(path),
    })),
  };
}
